// ============================================================================
// FOLIO — a stay's account (PMS Phase 2 slice 5, prerequisite A5)
// ============================================================================
// Every charge and payment in date order with a running balance, the source of
// each line (Front Office, restaurant outlet and bill, advance and receipt), who
// it is billed to, and the frozen tax. The desk works the account here:
//   Post charge      (post-charge)     — PostChargeModal, taxed by the server
//   Reverse          (reverse-charge)  — whole Front Office line, with a reason;
//                                        restaurant lines are voided in DineOps
//   Receive payment  (receive-payment) — the payment screen: receipt number,
//                                        Fairbooks and the folio line together
//   Room charging    (posting-control) — off switch and credit limit, with a
//                                        reason, shown to the desk and audited
//   Vouchers (7c)    — every desk charge has a numbered Folio Charge Voucher,
//                      printed from its line; after checkout, inside the late-charge
//                      window, a late charge may still be posted (post-late-charge)
// "Guest owes" is the guest's share: charges billed to a party are invoiced to
// it at checkout (slice 7) and are not collected from the guest.

import React, { useCallback, useEffect, useState } from 'react';
import { invoiceService } from 'api/services/invoiceService';
import { invoiceActions, openPdf } from 'components/modals/modules/pms-module/IssueInvoicesModal';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import PaymentCaptureModal from 'components/modals/modules/payment-module/PaymentCaptureModal';
import PostChargeModal from './PostChargeModal';
import RoomChargingModal from './RoomChargingModal';
import { folioService } from 'api/services/folioService';
import { formatCurrency } from 'utils/currencyFormat';
import { settlementModeLabel } from 'api/types/settlement.types';
import { INHOUSE_ACTIONS, folioSourceLabel, type FolioDetail, type FolioLine } from 'api/types/folio.types';

interface Props {
  isOpen: boolean;
  folioId: string | null;
  allowedActions: string[];
  onClose: () => void;
  /** After anything that changes the balance, so the list behind can refresh. */
  onChanged?: () => void;
}

const fmtDate = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const Tile: React.FC<{ label: string; value: string; tone?: 'warn' | 'muted' }> = ({ label, value, tone }) => (
  <div style={{ flex: '1 1 120px', padding: '0.5rem 0.75rem', borderRadius: '6px', background: 'var(--bg-secondary)' }}>
    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</div>
    <div style={{ fontSize: '1.05rem', fontWeight: 600, color: tone === 'warn' ? 'var(--danger, #dc2626)' : 'var(--text-primary)' }}>{value}</div>
  </div>
);

const FolioModal: React.FC<Props> = ({ isOpen, folioId, allowedActions, onClose, onChanged }) => {
  const can = (a: string) => allowedActions.includes(a);
  const [folio, setFolio] = useState<FolioDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [hideReversed, setHideReversed] = useState(false);
  const [payerFilter, setPayerFilter] = useState<'ALL' | 'GUEST' | 'PARTY'>('ALL');

  const load = useCallback(async () => {
    if (!folioId) return;
    setLoading(true);
    try {
      setFolio(await folioService.get(folioId));
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Could not open the folio.');
    } finally {
      setLoading(false);
    }
  }, [folioId]);

  useEffect(() => { if (isOpen) { setFolio(null); void load(); } }, [isOpen, load]);

  const changed = useCallback(() => { void load(); onChanged?.(); }, [load, onChanged]);

  // ---- post charge / receive payment ---------------------------------------------------
  const [posting, setPosting] = useState(false);
  const [receiving, setReceiving] = useState(false);

  // ---- reverse ------------------------------------------------------------------------
  const [reversing, setReversing] = useState<FolioLine | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const reverse = useCallback(async () => {
    if (!reversing || reason.trim().length < 3) return;
    setBusy(true);
    try {
      const r = await folioService.reverseCharge(reversing.id, reason.trim());
      if (!r.success) { notificationService.toast.warning(r.message || 'The folio refused the reversal.'); return; }
      notificationService.toast.success('Charge reversed.');
      setReversing(null);
      changed();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Reversal failed.');
    } finally {
      setBusy(false);
    }
  }, [reversing, reason, changed]);

  // ---- room charging -------------------------------------------------------------------
  const [controlOpen, setControlOpen] = useState(false);

  if (!isOpen) return null;

  const lines = (folio?.lines ?? []).filter((l) => {
    if (hideReversed && (l.reversed || l.reversalOfId)) return false;
    if (payerFilter === 'ALL') return true;
    // Payments are the guest's: the party is invoiced at checkout, not collected here.
    if (l.kind === 'PAYMENT') return payerFilter === 'GUEST';
    const guest = !l.billToType || l.billToType === 'GUEST';
    return payerFilter === 'GUEST' ? guest : !guest;
  });
  const overLimit = !!folio && folio.creditLimit != null && folio.currentBalance > folio.creditLimit;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            Folio {folio?.folioNumber ?? ''}
            {folio && <span className={`badge ${folio.isOpen ? 'badge-success' : 'badge-secondary'}`} style={{ marginLeft: '0.5rem', fontSize: '0.7rem' }}>{folio.folioStatusName || folio.folioStatusCode}</span>}
          </h2>
          <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          {loading && !folio && <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>}
          {folio && (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem 1.25rem', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                <span><b>{folio.guestFullName || 'Guest'}</b>{folio.partyName && <> · {folio.partyName}</>}</span>
                <span>Room {folio.roomNumbers || '—'}</span>
                <span>{fmtDate(folio.openedOn)} → {fmtDate(folio.expectedCheckOut)}</span>
                <span>{folio.mealPlanSummary || '—'} · {folio.adults}A{folio.children ? ` ${folio.children}C` : ''}</span>
                <span>Booking {folio.bookingNumber}{folio.grcNumber && <> · GRC {folio.grcNumber}</>}</span>
                <span className="badge badge-info" title="Who settles the stay">{settlementModeLabel(folio.settlementMode) || 'Guest pays'}</span>
                <span className="badge badge-secondary" title="Local date-time minus the property's business-day cutoff">Business date {fmtDate(folio.businessDate)}</span>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <Tile label="Charges" value={formatCurrency(folio.totalCharges)} />
                <Tile label="Payments" value={formatCurrency(folio.totalPayments)} />
                <Tile label="Balance" value={formatCurrency(folio.currentBalance)} tone={overLimit ? 'warn' : undefined} />
                {folio.partyCharges !== 0 && <Tile label={`Billed to ${folio.partyName || 'party'}`} value={formatCurrency(folio.partyCharges)} />}
                <Tile label="Guest owes" value={formatCurrency(Math.max(0, folio.guestBalance))} />
              </div>

              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', fontSize: '0.82rem',
                padding: '0.5rem 0.75rem', borderRadius: '6px', marginBottom: '0.75rem',
                background: folio.noPost ? 'var(--warning-bg, #fef3c7)' : 'var(--bg-secondary)',
                color: folio.noPost ? 'var(--warning-text, #92400e)' : 'var(--text-secondary)',
              }}>
                <span>
                  Room charging <b>{folio.noPost ? 'OFF — outlets must collect directly (desk charges still post)' : 'on'}</b>
                  {' · '}Credit limit <b>{folio.creditLimit != null ? formatCurrency(folio.creditLimit) : 'none'}</b>
                  {overLimit && <b style={{ color: 'var(--danger, #dc2626)' }}> (exceeded)</b>}
                </span>
                {folio.postingControlAt && (
                  <span title={folio.postingControlReason ?? ''}>
                    — {folio.postingControlByName || 'someone'}, {fmtDate(folio.postingControlAt)}: “{folio.postingControlReason}”
                  </span>
                )}
                {folio.isOpen && can(INHOUSE_ACTIONS.postingControl) && (
                  <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setControlOpen(true)}>Change</button>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                {folio.isOpen && can(INHOUSE_ACTIONS.postCharge) && (
                  <button className="btn btn-primary btn-sm" onClick={() => setPosting(true)}><IconSystem.add size={14} /> Post charge</button>
                )}
                {!folio.isOpen && folio.canPostLate && can(INHOUSE_ACTIONS.postCharge) && can(INHOUSE_ACTIONS.postLateCharge) && (
                  <button className="btn btn-primary btn-sm" onClick={() => setPosting(true)}
                    title={`Checked out ${fmtDate(folio.checkedOutOn)} — late charges up to ${folio.lateChargeDays} days after checkout`}>
                    <IconSystem.add size={14} /> Late charge
                  </button>
                )}
                {folio.isOpen && can(INHOUSE_ACTIONS.receivePayment) && (
                  <button className="btn btn-secondary btn-sm" onClick={() => setReceiving(true)}><IconSystem.creditCard size={14} /> Receive payment</button>
                )}
                {invoiceActions().includes('view') && (
                  <button className="btn btn-secondary btn-sm" title="Running folio for the guest — every line, payment and the balance"
                    onClick={() => { void openPdf(() => invoiceService.statement(folio.bookingId), 'The statement did not print.'); }}>
                    <IconSystem.print size={14} /> Statement
                  </button>
                )}
                {invoiceActions().includes('view') && (
                  <button className="btn btn-secondary btn-sm" title="Every folio line, night by night"
                    onClick={() => { void openPdf(() => invoiceService.statement(folio.bookingId, true), 'The statement did not print.'); }}>
                    Detailed
                  </button>
                )}
                <select className="form-input" style={{ width: 'auto', height: '2rem', fontSize: '0.8rem' }} value={payerFilter}
                  onChange={(e) => setPayerFilter(e.target.value as 'ALL' | 'GUEST' | 'PARTY')}>
                  <option value="ALL">All lines</option>
                  <option value="GUEST">Billed to guest</option>
                  <option value="PARTY">Billed to party</option>
                </select>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8rem' }}>
                  <input type="checkbox" checked={hideReversed} onChange={(e) => setHideReversed(e.target.checked)} /> Hide reversed
                </label>
                {!folio.taxCalculationEnabled && (
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>Tax calculation is off for this property.</span>
                )}
              </div>

              <div style={{ overflowX: 'auto', border: '1px solid var(--border-color)', borderRadius: '6px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-secondary)', textAlign: 'left' }}>
                      <th style={{ padding: '0.45rem 0.6rem' }}>Date</th>
                      <th style={{ padding: '0.45rem 0.6rem' }}>Description</th>
                      <th style={{ padding: '0.45rem 0.6rem' }}>Source</th>
                      <th style={{ padding: '0.45rem 0.6rem' }}>Billed to</th>
                      <th style={{ padding: '0.45rem 0.6rem', textAlign: 'right' }}>Charge</th>
                      <th style={{ padding: '0.45rem 0.6rem', textAlign: 'right' }}>Payment</th>
                      <th style={{ padding: '0.45rem 0.6rem', textAlign: 'right' }}>Balance</th>
                      <th style={{ padding: '0.45rem 0.6rem', width: 110 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {lines.length === 0 && (
                      <tr><td colSpan={8} style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>No lines yet.</td></tr>
                    )}
                    {lines.map((l) => {
                      const struck = l.reversed;
                      return (
                        <tr key={`${l.kind}-${l.id}`} style={{ borderTop: '1px solid var(--border-color)', opacity: struck ? 0.6 : 1 }}>
                          <td style={{ padding: '0.4rem 0.6rem', whiteSpace: 'nowrap' }}>{fmtDate(l.lineDate)}</td>
                          <td style={{ padding: '0.4rem 0.6rem', textDecoration: struck ? 'line-through' : undefined }}>
                            {l.description}
                            {l.kind === 'CHARGE' && l.quantity !== 1 && <span style={{ color: 'var(--text-muted)' }}> · {l.quantity} × {formatCurrency(l.unitPrice)}</span>}
                            {l.taxSummary && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Net {formatCurrency(l.amount)} + {l.taxSummary} {formatCurrency(l.taxAmount)}</div>}
                            {l.reversalReason && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Reversed: {l.reversalReason}</div>}
                            {l.voucherNumber && (
                              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                                Voucher {l.voucherNumber}
                                {l.chargeNature === 'RECOVERY' && ' · recovery'}
                                {l.isReimbursement && ' · at cost (reimbursement)'}
                              </div>
                            )}
                            {l.kind === 'PAYMENT' && (l.paymentMode || l.receiptNumber) && (
                              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                                {l.paymentMode}{l.paymentReference ? ` · ${l.paymentReference}` : ''}{l.receiptNumber ? ` · ${l.receiptNumber}` : ''}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '0.4rem 0.6rem', whiteSpace: 'nowrap' }}>
                            {folioSourceLabel(l)}
                            {l.invoicedElsewhereRef && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Bill {l.invoicedElsewhereRef}</div>}
                          </td>
                          <td style={{ padding: '0.4rem 0.6rem' }}>
                            {l.kind === 'CHARGE' ? (!l.billToType || l.billToType === 'GUEST' ? 'Guest' : (l.billToName || l.billToType)) : ''}
                            {l.billingState === 'INVOICED_ELSEWHERE' && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Invoiced by restaurant</div>}
                          </td>
                          <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{l.debit ? formatCurrency(l.debit) : ''}</td>
                          <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{l.credit ? formatCurrency(l.credit) : ''}</td>
                          <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 500 }}>{formatCurrency(l.runningBalance)}</td>
                          <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {l.voucherId && (
                              <button className="btn btn-secondary btn-sm" title={`Print voucher ${l.voucherNumber ?? ''}`} style={{ marginRight: 4 }}
                                onClick={() => { void openPdf(() => folioService.voucherPdf(l.voucherId!), 'The voucher did not print.'); }}>
                                <IconSystem.print size={13} />
                              </button>
                            )}
                            {l.canReverse && can(INHOUSE_ACTIONS.reverseCharge) && (
                              <button className="btn btn-secondary btn-sm" title="Reverse this charge" onClick={() => { setReversing(l); setReason(''); }}>Reverse</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>

      <PostChargeModal isOpen={posting} folio={folio} allowedActions={allowedActions} onClose={() => setPosting(false)} onPosted={() => { setPosting(false); changed(); }} />

      {receiving && folio && (
        <PaymentCaptureModal
          isOpen={receiving}
          mode="create"
          collectionPoint="FRONT_OFFICE"
          bookingId={folio.bookingId}
          propertyId={folio.propertyId}
          bookingNumber={folio.bookingNumber}
          suggestedAmount={Math.max(0, folio.guestBalance)}
          onSave={() => { setReceiving(false); changed(); }}
          onClose={() => setReceiving(false)}
        />
      )}

      {reversing && (
        <>
          <div className="modal-backdrop" style={{ zIndex: 10060 }} />
          <div className="modal-container modal-sm" style={{ zIndex: 10061 }}>
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">Reverse charge</h2>
              <button className="modal-close" onClick={() => setReversing(null)} disabled={busy}><IconSystem.x size={20} /></button>
            </div>
            <div className="modal-content">
              <div style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                {reversing.description} — <b>{formatCurrency(reversing.debit)}</b> on {fmtDate(reversing.lineDate)}.
                A negating line is posted today; the original stays on the folio, struck through.
              </div>
              <label className="form-label">Reason <span className="text-danger">*</span></label>
              <input type="text" className="form-input" value={reason} maxLength={150} autoFocus
                placeholder="Printed on the folio" onChange={(e) => setReason(e.target.value)} />
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-secondary" onClick={() => setReversing(null)} disabled={busy}>Cancel</button>
              <button className="btn btn-danger" onClick={reverse} disabled={busy || reason.trim().length < 3}>{busy ? 'Reversing…' : 'Reverse'}</button>
            </div>
          </div>
        </>
      )}

      <RoomChargingModal
        isOpen={controlOpen && !!folio}
        label={folio?.folioNumber ?? ''}
        folioId={folio?.folioId}
        noPost={!!folio?.noPost}
        creditLimit={folio?.creditLimit}
        onClose={() => setControlOpen(false)}
        onSaved={(updated) => { setFolio(updated); setControlOpen(false); onChanged?.(); }}
      />
    </>
  );
};

export default FolioModal;
